// The production `SessionStore` adapter and its runtime validator (design DD8; session-
// handoff implementation-review Finding 2). `SessionController` trusts whatever its
// injected `SessionStore.get()` returns to be a well-formed `PersistedSession | null` —
// so the boundary that reads untrusted `window.sessionStorage` (this module, used by
// `main.tsx`) must *earn* that contract by validating the parsed JSON, not merely casting
// it. A stored `null`, an array, a legacy shape missing `isHost`, or a foreign object with
// an unknown `phase` would otherwise install credentials that match no D9 classification
// or select the wrong connecting terminal; instead each is treated as "no persisted
// session" and the poison value is dropped from storage. Lives here (not inline in
// `main.tsx`) so both production and tests exercise the same code — `main.tsx` is EXCUSED
// from a unit test (real storage/socket/GPU need a browser), so the validation is proven
// here.

import type { PersistedSession, SessionPhase, SessionStore } from './SessionController';

/** The four client phases a persisted record may carry (design DD5/DD8). */
const VALID_PHASES: readonly SessionPhase[] = ['lobby', 'connecting', 'in-match', 'finished'];

/**
 * Runtime-validates an untrusted parsed value against the `PersistedSession` shape,
 * returning a fresh well-typed record on success or `null` on ANY failure (design DD8).
 * Checks: a plain object (not null, not an array); string `token`; string `matchId`; role
 * exactly `'player' | 'spectator' | null`; `phase` one of the four-value enum; boolean
 * `isHost`. Returning `null` for anything else is what lets a malformed/foreign/legacy
 * storage value be treated as "no persisted session" rather than crashing rehydration or
 * driving the wrong terminal.
 */
export function validatePersistedSession(value: unknown): PersistedSession | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.token !== 'string') {
    return null;
  }
  if (typeof record.matchId !== 'string') {
    return null;
  }
  if (record.role !== 'player' && record.role !== 'spectator' && record.role !== null) {
    return null;
  }
  if (typeof record.phase !== 'string' || !VALID_PHASES.includes(record.phase as SessionPhase)) {
    return null;
  }
  if (typeof record.isHost !== 'boolean') {
    return null;
  }
  return {
    token: record.token,
    matchId: record.matchId,
    role: record.role,
    phase: record.phase as SessionPhase,
    isHost: record.isHost,
  };
}

/** The subset of the DOM `Storage` API this adapter needs — accepted as a parameter so
 * `main.tsx` passes a `window.sessionStorage` shim and tests pass an in-memory double. */
export type SessionStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * Builds a `SessionStore` (design DD8) over a `Storage`-like backing, JSON-round-tripping
 * the `{ token, matchId, role, phase, isHost }` record under `key`. `get()` parses and
 * **runtime-validates** the stored value: a missing value returns `null`; malformed JSON
 * or a value that fails `validatePersistedSession` is treated as "no persisted session"
 * **and the poison key is cleared**, so a stale/foreign/legacy record never lingers to be
 * re-read. Every storage access is guarded so a throwing backend (private browsing, quota,
 * a sandboxed `SecurityError`) degrades to "nothing persisted" rather than crashing boot.
 */
export function createSessionStorageStore(storage: SessionStorageLike, key: string): SessionStore {
  const drop = (): void => {
    try {
      storage.removeItem(key);
    } catch {
      // Storage disabled/unavailable — nothing more we can do.
    }
  };

  return {
    get(): PersistedSession | null {
      let raw: string | null;
      try {
        raw = storage.getItem(key);
      } catch {
        return null; // storage unavailable — treat as nothing persisted
      }
      if (raw === null) {
        return null;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        drop(); // malformed JSON — drop the poison value
        return null;
      }
      const validated = validatePersistedSession(parsed);
      if (validated === null) {
        drop(); // wrong/foreign/legacy shape — drop it and start fresh
        return null;
      }
      return validated;
    },
    set(record: PersistedSession): void {
      try {
        storage.setItem(key, JSON.stringify(record));
      } catch {
        // Storage disabled/full: the session just does not survive a reload.
      }
    },
    clear(): void {
      drop();
    },
  };
}
